// src/merch/MerchApp.jsx
//
// MerchMod. Flow:
//   1. Product (product → size → inventory colour)
//   2. Design, per zone (shaft/blade × front/back): images, text, drawing,
//      templates, colouring sheets
//   3. Order (print-ready cut sheet → review → print)

import React, { useEffect, useRef, useState } from "react";
import { useSnapshot } from "valtio";
import { TextLayerDialog, openTextDialog, SPORTS_FONT_ORDER } from "../engine";
import {
  merch, setProduct, setSize, setColour, setHand, addImageFile, addText, updateText,
  getLayer, selectLayer, updateTransform, removeLayer, moveLayer, layersForSide, toggleFlip, toggleLink, toggleVisible, setOpacity, setBlend, duplicateLayer,
} from "./store";
import { PRODUCTS, getProduct, getSize, getColour, getSides, printAreaFor, printSizeFor, BLEED_MM } from "./products";
import { switchSide, copyFromSide } from "./sides";
import { useZoneComposites, buildPrintCanvas } from "./usePrintComposite";
import { useContour } from "./useContour";
import ProductViewer from "./ProductViewer.jsx";
import { commitPaintAsLayer } from "./paint";
import PrintAreaEditor from "./PrintAreaEditor.jsx";
import PaintTools from "./PaintTools.jsx";
import FullscreenEditor from "./FullscreenEditor.jsx";
import TemplatePicker from "./TemplatePicker.jsx";
import OrderDialog from "./OrderDialog.jsx";
import { buildSheetPdf } from "./checkout";
import { buildColouringSheetPdf } from "./colouringSheet";
import ScanImportDialog from "./ScanImportDialog.jsx";
import LogoPicker from "./LogoPicker.jsx";
import SaveDialog from "./SaveDialog.jsx";
import FaceDialog from "./FaceDialog.jsx";
import { StoreLogo, DemoChip, StoreFooter } from "./StoreBrand.jsx";
import { STORE } from "./stores";
import { preloadFaceTools } from "./removeBg";
import JerseyPanel from "./JerseyPanel.jsx";
import { applyJersey, ensureDefaultJersey, currentJersey, resolveColour } from "./jersey";
import { startOver } from "./store";
import { clearPaint } from "./paint";
import MobileDrawer, { useIsMobile } from "./MobileDrawer.jsx";

// phones get the full-screen editor automatically when they start drawing
// print-file downloads are for us, not customers (they'd take the file
// elsewhere): visible on localhost or with ?dev=1
// Checkout switches on once Jon's Stripe is connected: set
// VITE_MERCH_CHECKOUT=on in Vercel. Until then "Order" shows the print file.
const CHECKOUT_LIVE = import.meta.env.VITE_MERCH_CHECKOUT === "on";

// Print-file downloads (Print PDF / PNG) are for us only. Once checkout is
// live, customers can NEVER download print-ready art (no taking it to
// another printer): only on localhost, and ?dev stops working in production.
const DEV = typeof window !== "undefined" &&
  (/^(localhost|127\.)/.test(window.location.hostname) ||
    (!CHECKOUT_LIVE && new URLSearchParams(window.location.search).has("dev")));

const isPhone = () => typeof window !== "undefined" && Math.min(window.innerWidth, window.innerHeight) < 600;

const Eye = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M1.5 12S5.5 5 12 5s10.5 7 10.5 7-4 7-10.5 7S1.5 12 1.5 12Z" fill="none" stroke="currentColor" strokeWidth="2" /><circle cx="12" cy="12" r="3" fill="currentColor" /></svg>
);
const EyeOff = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M1.5 12S5.5 5 12 5s10.5 7 10.5 7-4 7-10.5 7S1.5 12 1.5 12Z" fill="none" stroke="currentColor" strokeWidth="2" opacity=".45" /><path d="M3 3l18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
);

/* Header tagline: a slow marquee of "You design it · We deliver it".
   One phrase in each run is red; it stops for reduced motion and on
   hover, and phones get one static line. */
/* step header: number in a circle, a check once it's done */
function Step({ n, title, done = false }) {
  return (
    <div className="ma-step">
      <span className={`ma-step-num ${done ? "done" : ""}`}>{done ? <CheckIcon size={13} /> : n}</span>
      <span className="ma-step-title">{title}</span>
    </div>
  );
}
const CheckIcon = ({ size = 12 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true"><path d="m5 12 4 4L19 6" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
);

const TAG = STORE.tagline || ["You design it", "We deliver it"];
function Tagline() {
  const phrase = (red, k) => (
    <span key={k} className={`ma-tag-phrase ${red ? "red" : ""}`}>
      <b>{TAG[0]}</b> <span>{TAG[1]}</span><i aria-hidden="true">·</i>
    </span>
  );
  const run = (h) => [0, 1, 2, 3].map((n) => phrase(n === 1, `${h}${n}`));
  return (
    <div className="ma-tagline" aria-label={`${TAG[0]}. ${TAG[1]}.`}>
      <div className="ma-tag-track" aria-hidden="true">
        <div className="ma-tag-run">{run("a")}</div>
        <div className="ma-tag-run">{run("b")}</div>
      </div>
      <div className="ma-tag-static" aria-hidden="true">{phrase(true, "s")}</div>
    </div>
  );
}

export default function MerchApp({ cancelled = false }) {
  const snap = useSnapshot(merch);
  const product = getProduct(snap.productId);
  const size = getSize(product, snap.sizeId);
  const colour = getColour(product, snap.colourId);
  // zones ("sides"): e.g. Shaft/Blade × Front/Back, each its own sticker
  const baseSides = getSides(product, size, snap.hand);
  // One composite per zone (the 3D model shows them all). The active zone
  // re-renders on every edit; parked zones only when they change.
  // 3D refreshes when a stroke finishes; the flat editor shows paint live.
  const composites = useZoneComposites(baseSides.map((s) => s.id), (baseSides.find((s) => s.id === snap.side) || baseSides[0]).id, snap.rev, snap.sideRev, snap.paint.strokes);
  // die-cut products: the cut line follows the artwork (worked out live)
  const contours = useContour(baseSides, composites, snap.contour);
  const sides = baseSides.map((s) => (s.sticker?.contour ? { ...s, sticker: { ...s.sticker, cutRings: contours[s.id]?.rings || null } } : s));
  const hasBack = sides.length > 1;
  const curSide = sides.find((s) => s.id === snap.side) || sides[0];
  const sticker = curSide.sticker;
  const area = printAreaFor(sticker);
  // the matching face on the same part (shaft ↔ shaft) for mirror/copy
  const otherSide = sides.find((s) => s.id === curSide.pair) || (sides.length === 2 ? sides.find((s) => s.id !== curSide.id) : null);
  const sideLayers = (id) => (id === snap.side ? snap.layers : snap.sideLayers[id] || []);
  const anyDesign = sides.some((s) => sideLayers(s.id).length) || snap.paint.strokes > 0;
  const parts = [...new Set(sides.map((s) => s.part || "main"))];
  // short side names ("Front", or "Shaft front" on the stick) — the product
  // name is already in the headings
  const cap = (t) => (t ? t[0].toUpperCase() + t.slice(1) : "");
  const shortSide = (z) => (z?.face ? (parts.length > 1 && z.part ? `${cap(z.part)} ${z.face}` : cap(z.face)) : z?.label || "");

  const composite = composites[curSide.id] || null;
  const fileRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [fs, setFs] = useState(false);
  const [viewFs, setViewFs] = useState(false); // 3D view full screen
  // ↻: remount the 3D view and rebuild every side's art (also runs on its
  // own if the browser drops the GPU context)
  const [viewerKey, setViewerKey] = useState(0);
  const refreshViewer = () => { setViewerKey((k) => k + 1); merch.sideRev++; merch.rev++; };
  // draggable split between the 3D view and the design pane (remembered)
  const viewerRef = useRef(null);
  const [viewerH, setViewerH] = useState(() => {
    try { const v = Number(localStorage.getItem("mm_viewer_h")); return v > 0 ? v : null; } catch { return null; }
  });
  useEffect(() => {
    try { viewerH ? localStorage.setItem("mm_viewer_h", String(Math.round(viewerH))) : localStorage.removeItem("mm_viewer_h"); } catch { /* private mode */ }
  }, [viewerH]);
  function startSplit(e) {
    e.preventDefault();
    const startY = e.clientY;
    const startH = viewerRef.current?.getBoundingClientRect().height || 400;
    const el = e.currentTarget;
    el.setPointerCapture?.(e.pointerId);
    const move = (ev) => {
      const max = window.innerHeight - 140, min = 140;
      setViewerH(Math.max(min, Math.min(max, startH + (ev.clientY - startY))));
    };
    const up = () => { el.removeEventListener("pointermove", move); el.removeEventListener("pointerup", up); el.removeEventListener("pointercancel", up); };
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerup", up);
    el.addEventListener("pointercancel", up);
  }
  // ornament etc.: start with the painted jersey on (once the saved design
  // has loaded, and only if the design is empty)
  useEffect(() => { if (snap.loaded) ensureDefaultJersey(); }, [snap.loaded, snap.productId, snap.sizeId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!viewFs) return;
    const onKey = (e) => { if (e.key === "Escape") setViewFs(false); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [viewFs]);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);
  const [scanFile, setScanFile] = useState(null);
  const [logosOpen, setLogosOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [faceFile, setFaceFile] = useState(null);
  // products with a face spot: fetch the face models in the background once
  // the page has settled, so cropping the first photo is quick
  const hasFace = !!product?.faceAnchor;
  useEffect(() => {
    if (!hasFace) return;
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 2500));
    const h = idle(() => preloadFaceTools(), { timeout: 6000 });
    return () => (window.cancelIdleCallback ? window.cancelIdleCallback(h) : clearTimeout(h));
  }, [hasFace]);
  const faceRef = useRef(null);
  const scanRef = useRef(null);
  const [showCancelled, setShowCancelled] = useState(cancelled);
  const [viewNonce, setViewNonce] = useState(0);
  const mobile = useIsMobile(); // every Front/Back click re-aims the 3D camera

  const selected = snap.layers.find((l) => l.id === snap.selectedId && !l.locked) || null;
  // the jersey's main colours as one-tap text colours (names / numbers
  // usually use them): jersey, stripes, chest band, without repeats
  const teamColours = (() => {
    const c = currentJersey(snap);
    if (!c) return null;
    const out = [];
    for (const [key, name] of [["jersey", "Jersey"], ["stripe-1", "Stripes"], ["stripe-2", "Chest band"], ["pants", "Pants"]]) {
      const hex = resolveColour(c, key);
      if (hex && !out.some(([, h]) => h.toLowerCase() === hex.toLowerCase())) out.push([name, hex]);
      if (out.length === 3) break;
    }
    return out.length ? out : null;
  })();

  function download(blob, filename) {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 4000);
  }

  // Print-ready PDF: artwork (trim + bleed) + CutContour cut line.
  async function exportPdf() {
    setExporting(true);
    try {
      if (merch.paint.strokes) commitPaintAsLayer(); // don't leave a drawing out of the print file
      // every side that has a design → one sheet, stacked, each with its own cut
      const printSides = sides.filter((s) => layersForSide(s.id).length);
      if (!printSides.length) return;
      const { blob } = await buildSheetPdf({ product, size, printSides });
      const tag = hasBack ? `_${printSides.map((s) => s.id).join("-")}` : "";
      download(blob, `MerchMod_${product.id}_${size.id}${tag}.pdf`);
    } finally {
      setExporting(false);
    }
  }

  // Open the merged print PDF (all sides + cut lines) in a new tab. The tab
  // is opened BEFORE the async work so mobile Safari doesn't block it.
  async function previewPdf() {
    const win = window.open("", "_blank");
    setExporting(true);
    try {
      if (merch.paint.strokes) commitPaintAsLayer();
      const printSides = sides.filter((s) => layersForSide(s.id).length);
      if (!printSides.length) { win?.close(); return; }
      const { blob } = await buildSheetPdf({ product, size, printSides });
      const url = URL.createObjectURL(blob);
      if (win) win.location.href = url;
      else download(blob, `MerchMod_${product.id}_${size.id}.pdf`);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      win?.close();
      throw e;
    } finally {
      setExporting(false);
    }
  }

  async function exportPng() {
    setExporting(true);
    try {
      if (merch.paint.strokes) commitPaintAsLayer();
      const c = await buildPrintCanvas(area, sticker, BLEED_MM, printSizeFor(sticker));
      const blob = await new Promise((res) => c.toBlob(res, "image/png"));
      if (blob) download(blob, `MerchMod_${product.id}_${size.id}${hasBack ? `_${snap.side}` : ""}.png`);
    } finally {
      setExporting(false);
    }
  }

  const productPanel = (
    <>
          <Step n="1" title="Product" done />
          <div className="ma-product-grid">
            {PRODUCTS.map((p) => {
              const st = p.sizes[0]?.sticker || p.sizes[0]?.zones?.[0];
              const sub = p.id === "ornament" && st ? `${Math.round(st.widthMm)} × ${Math.round(st.heightMm)} mm`
                : p.sizes.length > 1 ? `${p.sizes.length} sizes` : p.sizes[0]?.label || "Custom";
              const on = p.id === product.id;
              return (
                <button key={p.id} className={`ma-product ${on ? "selected" : ""}`} aria-pressed={on} onClick={() => setProduct(p.id)}>
                  <span className="ma-product-icon">{p.name[0]}</span>
                  <span><strong>{p.name}</strong><small>{sub}</small></span>
                  {on && <span className="ma-product-check"><CheckIcon size={11} /></span>}
                </button>
              );
            })}
          </div>
          <p className="ma-muted">{product.blurb}</p>

          {product.sizes.length > 1 && (
            <>
              <h3>Size</h3>
              <div className="ma-seg full">
                {product.sizes.map((s) => (
                  <button
                    key={s.id}
                    aria-pressed={s.id === size.id}
                    disabled={s.comingSoon}
                    title={s.comingSoon ? "Coming soon" : undefined}
                    onClick={() => setSize(s.id)}
                  >
                    {s.label}{s.comingSoon ? " · soon" : ""}
                  </button>
                ))}
              </div>
            </>
          )}

          {product.handed && (
            <>
              <h3>Shot</h3>
              <div className="ma-seg full">
                {["left", "right"].map((h) => (
                  <button key={h} aria-pressed={snap.hand === h} onClick={() => setHand(h)}>
                    {h === "left" ? "Left" : "Right"}
                  </button>
                ))}
              </div>
              <p className="ma-muted">Which side you hold the stick on. Not sure? Most players in Canada shoot left.</p>
            </>
          )}

          <h3>Colour</h3>
          <div className="ma-swatches">
            {product.colours.map((c) => (
              <button
                key={c.id}
                className="ma-swatch"
                title={c.inStock ? c.label : `${c.label} — out of stock`}
                aria-pressed={c.id === colour.id}
                disabled={!c.inStock}
                onClick={() => setColour(c.id)}
                style={{ background: c.hex }}
              />
            ))}
          </div>
          <p className="ma-muted">{colour.label}</p>

    </>
  );

  const designPanel = (
    <>
          <Step n="2" title="Design" done={anyDesign} />
          {hasBack && (
            <div className="ma-sides">
              <div className="ma-field-row"><h3>Side</h3><span className="ma-hint">{shortSide(curSide)} · {Math.round(sticker.widthMm)} × {Math.round(sticker.heightMm)} mm</span></div>
              <div className="ma-zones">
                {parts.map((part) => (
                  <div className="ma-zone-row" key={part}>
                    {parts.length > 1 && <span>{part === "main" ? "" : part[0].toUpperCase() + part.slice(1)}</span>}
                    <div className="ma-seg zones full">
                      {sides.filter((s) => (s.part || "main") === part).map((s) => (
                        <button key={s.id} aria-pressed={s.id === snap.side} onClick={() => { switchSide(s.id); setViewNonce((n) => n + 1); }}>
                          {s.face ? s.face[0].toUpperCase() + s.face.slice(1) : s.label}
                          {sideLayers(s.id).length ? <i className="ma-zone-dot" /> : null}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {otherSide && (
                <div className="ma-side-tools">
                  <button
                    className="ma-mini-btn"
                    disabled={!sideLayers(otherSide.id).length}
                    title={`Copy the ${otherSide.label.toLowerCase()} design here, mirrored. Text stays readable.`}
                    onClick={() => {
                      if (snap.layers.length && !window.confirm(`Replace the ${curSide.label.toLowerCase()} design?`)) return;
                      copyFromSide(otherSide.id, { mirror: true });
                    }}
                  >
                    ⇋ Mirror {shortSide(otherSide).toLowerCase()}
                  </button>
                  <button
                    className="ma-mini-btn"
                    disabled={!sideLayers(otherSide.id).length}
                    onClick={() => {
                      if (snap.layers.length && !window.confirm(`Replace the ${curSide.label.toLowerCase()} design?`)) return;
                      copyFromSide(otherSide.id);
                    }}
                  >
                    Copy {shortSide(otherSide).toLowerCase()}
                  </button>
                </div>
              )}
            </div>
          )}
          {product.template && <JerseyPanel product={product} />}
          {sticker.contour && (
            <div className="ma-cutcard">
              <div className="ma-cutcard-head">✂️ <b>Cut line</b> <span className="ma-muted">follows your design</span></div>
              <div className="ma-jersey-style">
                <span>Border</span>
                <div className="ma-seg small" role="group" aria-label="Border around the design">
                  {[["1/16\"", 1.5875], ["1/8\"", 3.175], ["3/16\"", 4.7625], ["1/4\"", 6.35]].map(([lbl, mm]) => (
                    <button key={lbl} aria-pressed={Math.abs((snap.contour?.offsetMm ?? 3.175) - mm) < 0.01} onClick={() => { merch.contour = { ...merch.contour, offsetMm: mm }; }}>{lbl}</button>
                  ))}
                </div>
              </div>
              <div className="ma-jersey-style">
                <span>Shape</span>
                <div className="ma-seg small" role="group" aria-label="Cut shape">
                  <button aria-pressed={snap.contour?.single !== false} onClick={() => { merch.contour = { ...merch.contour, single: true }; }} title="One sticker: a single shape around everything">One piece</button>
                  <button aria-pressed={snap.contour?.single === false} onClick={() => { merch.contour = { ...merch.contour, single: false }; }} title="Pieces far apart get their own cut line">Separate pieces</button>
                </div>
              </div>
              {(contours[curSide.id]?.warnings || []).map((w) => <p key={w} className="ma-note">{w}</p>)}
              <p className="ma-muted ma-cut-tip">Keep your design inside the dashed box: that's the biggest the sticker can be.</p>
            </div>
          )}
          <div className="ma-row">
            <button className="ma-btn" onClick={() => fileRef.current?.click()}>Add image</button>
            <button className="ma-btn" onClick={() => openTextDialog()}>Add text</button>
            <button className="ma-btn" onClick={() => setTemplatesOpen(true)}>Templates</button>
            <button className="ma-btn" onClick={() => setLogosOpen(true)}>Team logos</button>
            {product.faceAnchor?.[curSide.id] && (
              <>
                <button className="ma-btn" onClick={() => faceRef.current?.click()}>🙂 Add a face</button>
                <input
                  ref={faceRef} type="file" accept="image/*,.heic,.heif" hidden
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) setFaceFile(f); e.target.value = ""; }}
                />
              </>
            )}
            <button
              className={`ma-btn ${snap.paint.active ? "primary" : ""}`}
              aria-pressed={snap.paint.active}
              onClick={() => {
                const on = !merch.paint.active;
                merch.paint.active = on;
                if (on && isPhone()) setFs(true);
              }}
            >
              {snap.paint.active ? "Drawing…" : "Draw"}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                addImageFile(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
          </div>

          <div className="ma-sheet-row">
            <span>🖍️ Colour it by hand</span>
            <button
              className="ma-link"
              onClick={() => download(buildColouringSheetPdf({ product, size, sides }), `MerchMod_colouring-sheet_${product.id}_${size.id}.pdf`)}
            >
              Print sheet
            </button>
            <button className="ma-link" onClick={() => scanRef.current?.click()}>Upload sheet</button>
            <input
              ref={scanRef} type="file" accept="image/*,.heic,.heif,application/pdf,.pdf" hidden
              onChange={(e) => { const f = e.target.files?.[0]; if (f) setScanFile(f); e.target.value = ""; }}
            />
          </div>

          {snap.paint.active && !fs && <PaintTools />}

          {snap.layers.length === 0 ? (
            <p className="ma-muted">Add an image, text or a drawing to start designing.</p>
          ) : (
            <ul className="ma-layers">
              {snap.layers.map((L, i) => (
                <li key={L.id} aria-selected={L.id === snap.selectedId} className={`${L.visible === false ? "is-hidden" : ""} ${L.link ? "is-linked" : ""} ${L.link && snap.layers[i + 1]?.link === L.link ? "link-next" : ""}`} onClick={() => selectLayer(L.id)}>
                  <button
                    className="ma-eye"
                    title={L.visible === false ? "Show layer" : "Hide layer (hidden layers don't print)"}
                    aria-pressed={L.visible !== false}
                    onClick={(e) => { e.stopPropagation(); toggleVisible(L.id); }}
                  >
                    {L.visible === false ? <EyeOff /> : <Eye />}
                  </button>
                  <span className="ma-layer-name" title={L.name}>{L.kind === "text" ? "T · " : ""}{L.locked ? "🔒 " : ""}{L.name}</span>
                  <span className="ma-layer-actions" onClick={(e) => e.stopPropagation()}>
                    {L.kind === "text" && <button title="Edit text" onClick={() => openTextDialog(L.id)}>Edit</button>}
                    {!L.locked && (L.link || (snap.layers[i + 1] && !snap.layers[i + 1].locked)) && (
                      <button
                        className="ma-link-btn"
                        title={L.link ? "Linked: moves with the layers marked 🔗 (tap to unlink)" : "Link with the layer below so they move, turn and resize together"}
                        aria-pressed={!!L.link}
                        onClick={() => toggleLink(L.id)}
                      >🔗</button>
                    )}
                    {!L.locked && <button className="ma-row-extra" title="Flip horizontally" aria-pressed={!!L.transform?.flipX} onClick={() => toggleFlip(L.id)}>⇋</button>}
                    {(!L.locked || L.kind === "jersey-hair") && <>
                      <button className="ma-row-extra" title="Move up" disabled={i === 0} onClick={() => moveLayer(L.id, "up")}>↑</button>
                      <button className="ma-row-extra" title="Move down" disabled={i === snap.layers.length - 1 || snap.layers[i + 1]?.locked} onClick={() => moveLayer(L.id, "down")}>↓</button>
                    </>}
                    <button title={L.locked ? "Remove the jersey (all sides)" : "Delete"} onClick={() => (L.kind === "jersey-mask" ? applyJersey({ ...(L.template?.colours || {}), _mask: undefined }) : L.kind === "jersey-hair" ? applyJersey({ ...(L.template?.colours || {}), _hair: undefined }) : L.kind === "jersey" || L.kind === "jersey-helmet" ? applyJersey(null) : removeLayer(L.id))}>✕</button>
                  </span>
                </li>
              ))}
            </ul>
          )}

          {selected && (
            <div className="ma-transform">
              <div className="ma-blend">
                <span>Blend</span>
                <div className="ma-seg small" role="group" aria-label="Blend mode">
                  {[
                    ["block", "Block", "Solid: covers what's underneath, and white stays white"],
                    ["ink", "Ink", "Multiply: white disappears, colours tint what's underneath (great for scanned drawings)"],
                    ["whitefill", "White", "Solid white shape: put a copy underneath for an outline or backing"],
                  ].map(([v, label, tip]) => (
                    <button
                      key={v} title={tip}
                      aria-pressed={(selected.blend === "skin" || selected.blend === "multiply" ? "ink" : selected.blend || "block") === v}
                      onClick={() => setBlend(selected.id, v)}
                    >{label}</button>
                  ))}
                </div>
              </div>
              <label>
                Opacity
                <input
                  type="range" min={0} max={100} step={1}
                  value={Math.round((selected.opacity ?? 1) * 100)}
                  onChange={(e) => setOpacity(selected.id, Number(e.target.value) / 100)}
                  onDoubleClick={() => setOpacity(selected.id, 1)}
                />
                <span className="ma-val">{Math.round((selected.opacity ?? 1) * 100)}%</span>
              </label>
              <label>
                Size
                <input
                  type="range" min={0.05} max={1.5} step={0.01}
                  value={selected.transform.scale}
                  onChange={(e) => updateTransform(selected.id, { scale: Number(e.target.value), scaleX: undefined, scaleY: undefined })}
                />
              </label>
              <label>
                Rotate
                <input
                  type="range" min={-180} max={180} step={1}
                  value={Math.round(((selected.transform.rot || 0) * 180) / Math.PI)}
                  onChange={(e) => updateTransform(selected.id, { rot: (Number(e.target.value) * Math.PI) / 180 })}
                  onDoubleClick={() => updateTransform(selected.id, { rot: 0 })}
                />
              </label>
              <div className="ma-row">
                <button className="ma-btn ghost" onClick={() => updateTransform(selected.id, { tx: 0.5, ty: 0.5 })}>
                  Centre
                </button>
                <button className="ma-btn ghost" onClick={() => duplicateLayer(selected.id)} title="Duplicate (⌘D)">
                  Duplicate
                </button>
              </div>
              <p className="ma-muted">Tip: drag it on the sticker, pull a corner to resize, spin the top knob — or pinch with two fingers.</p>
            </div>
          )}

    </>
  );

  const finishPanel = (
    <>
          <Step n="3" title="Finish" />
          <div className="ma-row">
            {DEV && (
              <>
            <button className="ma-btn" onClick={exportPdf} disabled={exporting || !anyDesign}>
              {exporting ? "Preparing…" : "Print PDF"}
            </button>
            <button className="ma-btn ghost" onClick={exportPng} disabled={exporting || !anyDesign}>
              PNG
            </button>
              </>
            )}
            {CHECKOUT_LIVE ? (
              <button className="ma-btn primary" disabled={!anyDesign} onClick={() => setOrderOpen(true)}>
                Order · ${product.price.toFixed(2)}
              </button>
            ) : (
              <>
                <button className="ma-btn primary" disabled={exporting || !anyDesign} onClick={previewPdf}>
                  {exporting ? "Preparing…" : "Preview print file"}
                </button>
                {!DEV && (
                  <button className="ma-btn ghost" disabled={exporting || !anyDesign} onClick={exportPdf}>
                    Download PDF
                  </button>
                )}
              </>
            )}
          </div>
          <p className="ma-muted">
            {CHECKOUT_LIVE
              ? "Every design is checked by hand before printing — you're only charged once it's approved."
              : "Ordering opens soon. Preview shows the print-ready file: every side, true size, with the cut line."}
          </p>
    </>
  );

  const stageContent = (
    <>
          {/* same element in and out of full screen, so the 3D view never reloads */}
          <div
            ref={viewerRef}
            className={`ma-viewer ${viewFs ? "is-fs" : ""}`}
            style={!viewFs && viewerH ? { height: viewerH, flex: "0 0 auto" } : undefined}
          >
            <ProductViewer
              key={viewerKey}
              product={product} colour={colour} zones={sides} composites={composites} activeZone={curSide.id} viewKey={viewNonce}
              onContextRestored={refreshViewer}
            />
            <button className="ma-view-refresh" onClick={refreshViewer} title="Refresh the 3D view" aria-label="Refresh the 3D view">↻</button>
            <button
              className="ma-fs-open ma-view-fs"
              onClick={() => setViewFs((v) => !v)}
              aria-label={viewFs ? "Exit full screen" : "Full screen 3D view"}
              title={viewFs ? "Exit full screen (Esc)" : "Full screen"}
            >
              {viewFs ? <>✕ <span>Close</span></> : <>⤢ <span>Full screen</span></>}
            </button>
          </div>
          {!viewFs && (
            <div
              className="ma-split"
              role="separator"
              aria-orientation="horizontal"
              aria-label="Resize the 3D view"
              title="Drag to resize · double-click to reset"
              onPointerDown={startSplit}
              onDoubleClick={() => setViewerH(null)}
            >
              <i />
            </div>
          )}
          {fs ? (
            <div className="ma-print-editor ma-fs-placeholder">Editing full screen…</div>
          ) : (
            <div className="ma-editor-card">
              <button className="ma-fs-open" onClick={() => setFs(true)} aria-label="Full screen editor" title="Full screen">
                ⤢ <span>Full screen</span>
              </button>
              <PrintAreaEditor
                key={curSide.id}
                guides={curSide.guides}
                area={area}
                sticker={sticker}
                colour={colour}
                composite={composite}
                selectedId={snap.selectedId}
                paintActive={snap.paint.active}
              />
            </div>
          )}
    </>
  );

  return (
    <div className={`ma-app ${mobile ? "is-mobile" : ""}`}>
      <header className="ma-header">
        <StoreLogo />
        <DemoChip />
        <Tagline />
        <button
          className="ma-btn ghost ma-new-btn"
          disabled={!anyDesign}
          title="Start a new design (clears every side)"
          onClick={async () => {
            if (!window.confirm("Start a new design? This clears everything on every side." + (snap.saved ? " Your saved project stays saved." : ""))) return;
            clearPaint();
            merch.paint.active = false;
            startOver();
            await ensureDefaultJersey(); // ornament starts with the painted jersey again
          }}
        >
          New
        </button>
        {snap.saved && <span className="ma-saved"><i /> Saved</span>}
        <button className="ma-btn ma-save-btn" onClick={() => setSaveOpen(true)} disabled={!anyDesign} title="Save your design and get a link to keep editing on any device">
          {snap.saved ? "Save" : "Save project"}
        </button>
        <button
          className="ma-btn ink ma-cta"
          disabled={!anyDesign || exporting}
          onClick={() => (CHECKOUT_LIVE ? setOrderOpen(true) : previewPdf())}
          title={CHECKOUT_LIVE ? "Order this design" : "See the print-ready file"}
        >
          {CHECKOUT_LIVE ? "Order" : "Preview"}
          <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true"><path d="m9 18 6-6-6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      </header>

      {showCancelled && (
        <div className="ma-banner">
          Checkout cancelled — no charge. Your design is right where you left it.
          <button onClick={() => { setShowCancelled(false); window.history.replaceState(null, "", window.location.pathname); }} aria-label="Dismiss">✕</button>
        </div>
      )}
      {mobile ? (
        <main className="ma-mobile">
          <section className="ma-stage ma-mstage">{stageContent}</section>
          <MobileDrawer
            tabs={[
              { id: "product", label: "Product", content: productPanel },
              { id: "design", label: "Design", content: designPanel },
              { id: "finish", label: "Finish", content: finishPanel },
            ]}
          />
        </main>
      ) : (
        <main className="ma-main ma-studio">
          <aside className="ma-controls">
            <div className="ma-intro">
              <span className="ma-eyebrow">Custom builder</span>
              <div className="ma-intro-title">Make it yours</div>
              <p>Choose a product, then make every detail your own: team colours, names, numbers, logos and faces.</p>
            </div>
            <section className="ma-ctl-section">{productPanel}</section>
            <section className="ma-ctl-section">{designPanel}</section>
            <section className="ma-ctl-section">{finishPanel}</section>
          </aside>
          <section className="ma-stage">
            <div className="ma-preview-head">
              <div>
                <span className="ma-eyebrow">Live preview</span>
                <div className="ma-preview-title">{product.name}</div>
              </div>
              <div className="ma-view-label">
                <span>{shortSide(curSide)}</span>
                <i />
                <span>{colour.label}</span>
              </div>
            </div>
            {stageContent}
          </section>
        </main>
      )}

      {fs && (
        <FullscreenEditor
          product={product}
          size={size}
          colour={colour}
          area={area}
          sticker={sticker}
          composite={composite}
          sides={sides}
          onAddImage={() => fileRef.current?.click()}
          onClose={() => setFs(false)}
        />
      )}
      {scanFile && (
        <ScanImportDialog file={scanFile} product={product} size={size} onClose={() => setScanFile(null)} />
      )}
      {orderOpen && (
        <OrderDialog product={product} size={size} colour={colour} onClose={() => setOrderOpen(false)} />
      )}
      {logosOpen && <LogoPicker area={area} onClose={() => setLogosOpen(false)} />}
      {saveOpen && <SaveDialog onClose={() => setSaveOpen(false)} />}
      {faceFile && (
        <FaceDialog file={faceFile} area={area} anchor={product.faceAnchor?.[curSide.id]} onClose={() => setFaceFile(null)} />
      )}
      {templatesOpen && (
        <TemplatePicker area={area} sticker={sticker} zones={sides} activeId={curSide.id} onClose={() => setTemplatesOpen(false)} />
      )}
      <StoreFooter />
      <TextLayerDialog getLayer={getLayer} onAdd={addText} onUpdate={updateText} addLabel="Add to design" previewBg="linear-gradient(135deg, #a4a4a4 0%, #8c8c8c 100%)" teamColours={teamColours} theme="light" fontOrder={SPORTS_FONT_ORDER} defaults={{ fontKey: "block", text: "NAME" }} />
    </div>
  );
}
